import styles from "./Player.module.css";
import { FaSignal } from "react-icons/fa";
import PlayerAvatar from "./PlayerAvatar";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";

function Player({
  elo,
  previousElo,
  displayName,
  gameEnded,
  avatarId,
  fbId,
  googleId,
  isUser,
  isGuest,
  id,
  avatarType,
  children,
  disconnected = false,
  disconnectTimeout = null,
  disconnectTimeoutStart = null,
}) {
  const { t } = useTranslation("common");
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    if (!disconnected || !disconnectTimeout || !disconnectTimeoutStart) {
      setRemainingTime(0);
      return;
    }

    const updateTimer = () => {
      const elapsed = Date.now() - disconnectTimeoutStart;
      const remaining = Math.max(
        0,
        Math.ceil((disconnectTimeout - elapsed) / 1000)
      );
      setRemainingTime(remaining);
    };

    // Update immediately
    updateTimer();

    // Then update every second
    const intervalId = setInterval(updateTimer, 1000);

    // Cleanup interval on unmount or when disconnected state changes
    return () => clearInterval(intervalId);
  }, [disconnected, disconnectTimeout, disconnectTimeoutStart]);

  function renderElo() {
    if (!elo) {
      return;
    }
    if (previousElo) {
      const diff = elo - previousElo;
      let diffTag;

      if (diff > 0) {
        diffTag = <span className={styles.diffEqual}>+{diff}</span>;
      } else if (diff < 0) {
        diffTag = <span className={styles.diffEqual}>{diff}</span>;
      } else {
        diffTag = <span className={styles.diffEqual}>0</span>;
      }

      return (
        <div className="inline-flex font-normal">
          <div className="pr-1 text-base">
            <FaSignal />
          </div>
          {elo} ({diffTag})
        </div>
      );
    } else {
      return (
        <div className="inline-flex font-normal">
          <div className="pr-1 text-base">
            <FaSignal />
          </div>
          {elo}
        </div>
      );
    }
  }

  const usernameStyle = gameEnded ? styles.usernameGameEnded : styles.username;

  return (
    <div className={styles.player}>
      <div className={`font-semibold bg-gray-200 ${styles.header}`}>
        <div className={usernameStyle}>{displayName}</div>
        {renderElo()}
      </div>
      <div className={styles.body}>
        <div
          className={
            disconnected ? "flex space-x-2 align-bottom animate-pulse" : ""
          }
        >
          <PlayerAvatar
            {...{
              isUser,
              avatarId,
              fbId,
              googleId,
              isGuest,
              id,
              avatarType,
              gameEnded,
              status: disconnected ? "offline" : "neutral",
            }}
          />
          {disconnected && (
            <div className="text-xs italic">
              <span className="hidden md:inline">
                {t("Reconnecting")}
                <br />
              </span>
              {remainingTime}s
            </div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}

export default Player;
